import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiArrowRight, FiTrendingDown, FiTrendingUp, FiClock, FiCheck, FiAlertTriangle } = FiIcons;

const FinalCTA = () => {
  const handleCTAClick = () => {
    window.open('https://app.noem.ai/cb/growthshop', '_blank');
  };

  return (
    <section className="py-20 bg-gradient-to-br from-slate-950 via-black to-slate-950 text-white relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 via-transparent to-orange-500/10 animate-pulse"></div>
      </div>

      <div className="container mx-auto px-6 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-400/30 text-red-400 px-6 py-3 rounded-full font-bold mb-8 backdrop-blur-lg">
            <SafeIcon icon={FiAlertTriangle} className="text-red-400" />
            THE CHOICE IS YOURS
          </div>
          
          <h2 className="text-4xl md:text-7xl font-black mb-8 leading-tight">
            In 30 Days, You'll Either Be{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
              Making Thousands Extra
            </span>
            <br />
            Or Looking at The{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-pink-400">
              Same Lost Revenue
            </span>
          </h2>
        </motion.div>

        {/* Comparison */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-16">
          {/* Without AI */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-gradient-to-br from-red-500/20 to-pink-500/20 backdrop-blur-xl rounded-3xl p-8 border border-red-400/30"
          >
            <div className="text-center mb-6">
              <SafeIcon icon={FiTrendingDown} className="text-red-400 text-5xl mx-auto mb-4" />
              <h3 className="text-2xl font-black text-red-400">If You Do Nothing</h3>
              <p className="text-gray-400">Your next 30 days look like this:</p>
            </div>
            
            <div className="space-y-4">
              <div className="bg-red-500/10 rounded-xl p-4 border border-red-400/20">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Lost Revenue</span>
                  <span className="text-red-400 font-black text-xl">$150,000</span>
                </div>
              </div>
              <div className="bg-red-500/10 rounded-xl p-4 border border-red-400/20">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Wasted Support Hours</span>
                  <span className="text-red-400 font-black text-xl">600+</span>
                </div>
              </div>
              <div className="bg-red-500/10 rounded-xl p-4 border border-red-400/20">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Growth</span>
                  <span className="text-red-400 font-black text-xl">Stagnant</span>
                </div>
              </div>
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-red-400 font-bold text-lg">😞 Same problems, same losses</p>
            </div>
          </motion.div>

          {/* With AI */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 backdrop-blur-xl rounded-3xl p-8 border border-emerald-400/30"
          >
            <div className="text-center mb-6">
              <SafeIcon icon={FiTrendingUp} className="text-emerald-400 text-5xl mx-auto mb-4" />
              <h3 className="text-2xl font-black text-emerald-400">With Our AI</h3>
              <p className="text-gray-400">Your next 30 days look like this:</p>
            </div>
            
            <div className="space-y-4">
              <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-400/20">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Recovered Revenue</span>
                  <span className="text-emerald-400 font-black text-xl">$127,500</span>
                </div>
              </div>
              <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-400/20">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Hours Saved</span>
                  <span className="text-emerald-400 font-black text-xl">480+</span>
                </div>
              </div>
              <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-400/20">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Growth</span>
                  <span className="text-emerald-400 font-black text-xl">+32%</span>
                </div>
              </div>
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-emerald-400 font-bold text-lg">🚀 Explosive growth & profits</p>
            </div>
          </motion.div>
        </div>

        {/* Final Decision Box */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="bg-gradient-to-r from-yellow-400 to-orange-400 rounded-3xl p-8 md:p-12 text-center text-black max-w-4xl mx-auto"
        >
          <h3 className="text-3xl md:text-4xl font-black mb-6">
            The Math Is Simple
          </h3>
          
          <div className="bg-white/20 backdrop-blur-lg rounded-2xl p-6 mb-8">
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div>
                <div className="text-sm font-medium mb-2">Your Investment Today:</div>
                <div className="text-4xl font-black">$250</div>
              </div>
              <div>
                <div className="text-sm font-medium mb-2">Your Return in 30 Days:</div>
                <div className="text-4xl font-black">$127,500</div>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-black/20">
              <div className="text-2xl font-black">
                That's a <span className="text-emerald-600">51,000% ROI</span>
              </div>
            </div>
          </div>

          {/* Urgency Timer */}
          <div className="bg-black/20 rounded-2xl p-4 mb-8">
            <div className="flex items-center justify-center gap-2">
              <SafeIcon icon={FiClock} className="text-xl" />
              <span className="font-bold">Black Friday offer expires in 2 days, 14 hours</span>
            </div>
          </div>

          {/* Final CTA */}
          <motion.button
            onClick={handleCTAClick}
            whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(0,0,0,0.3)" }}
            whileTap={{ scale: 0.95 }}
            className="group w-full bg-black text-white font-black py-6 px-12 rounded-2xl text-2xl shadow-2xl transition-all duration-300 flex items-center justify-center gap-3 cursor-pointer"
          >
            <span>GET MY AI ASSISTANT FOR $250</span>
            <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-2 transition-transform text-2xl" />
          </motion.button>

          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm font-medium">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiCheck} className="text-green-600" />
              <span>30-day guarantee</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiCheck} className="text-green-600" />
              <span>Setup in 7 days</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiCheck} className="text-green-600" />
              <span>No hidden fees</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCTA;